import { skipToken } from "@reduxjs/toolkit/query";

import { useGetAllDocTypesForEcrQuery } from "api/memberPortal/ecr/docs/ecrDocs.api";
import { EcrDoc } from "api/memberPortal/ecr/docs/ecrDocs.types";
import { useEcrQuery } from "api/memberPortal/ecr/ecr.api";
import { useGetAllTdsForEcrQuery } from "api/memberPortal/ecr/tds/ecrTds.api";
import { useGetFilesByTypeAndIdQuery } from "api/memberPortal/files/files.api";
import { UserUploadedFile } from "api/memberPortal/files/files.types";
import { TechnicalDecision } from "api/memberPortal/technicalDecisions/technicalDecisions.types";

const DEFAULT_DOCS = [] as EcrDoc[];
const DEFAULT_TDS = [] as TechnicalDecision[];
const DEFAULT_FILES = [] as UserUploadedFile[];

/**
 * Hook for fetching all edit-mode data for an ECR comment.
 * Fetches the ECR itself plus its related docs, TDs, and files.
 * Cascading data (PP options, TD options) is handled by useEcrCascadingData.
 */
export const useEcrFormData = (ecrid: number | undefined) => {
  const { data: ecr, isFetching: isFetchingEcr } = useEcrQuery(
    ecrid ?? skipToken,
  );
  const { data: existingDocs = DEFAULT_DOCS, isFetching: isFetchingDocs } =
    useGetAllDocTypesForEcrQuery(ecrid ?? skipToken);

  const { data: existingTds = DEFAULT_TDS, isFetching: isFetchingTds } =
    useGetAllTdsForEcrQuery(ecrid ?? skipToken);

  const { data: existingFiles = DEFAULT_FILES, isFetching: isFetchingFiles } =
    useGetFilesByTypeAndIdQuery(
      ecrid ? { file_type: "ecr-comment", file_type_id: ecrid } : skipToken,
    );

  const isLoading =
    isFetchingEcr || isFetchingDocs || isFetchingTds || isFetchingFiles;

  return {
    ecr,
    existingDocs,
    existingTds,
    existingFiles,
    isLoading,
  };
};
